import type {
  SearchContext_1_0_3,
  Filter_1_0_0,
  Sort_1_0_0,
} from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'

interface MerchandiseSearchContextOptions {
  filters: App.MerchandiseFilters
  searchId: string
  urlPathSlug?: string
}

export function mapMerchandiseFiltersToSearchContext({
  filters,
  searchId,
  urlPathSlug,
}: MerchandiseSearchContextOptions): SearchContext_1_0_3 {
  const naturalLanguageQuery = filters.name ?? urlPathSlug

  return {
    vertical: 'merchandise',
    searchId,
    naturalLanguageQuery,
  }
}

function addArrayFilter(
  contexts: Array<Filter_1_0_0>,
  type: string,
  label: string,
  values?: Array<string>,
): void {
  if (values?.length) {
    contexts.push({ type, label, value: values.join(',') })
  }
}

export function mapMerchandiseFiltersToFilterContexts(
  filters: App.MerchandiseFilters,
): Array<Filter_1_0_0> {
  const contexts: Array<Filter_1_0_0> = []

  addArrayFilter(contexts, 'category', 'Category', filters.categories)
  addArrayFilter(contexts, 'brand', 'Brand', filters.brands)
  addArrayFilter(contexts, 'campaign', 'Campaign', filters.campaigns)
  addArrayFilter(contexts, 'tag', 'Tag', filters.tags)

  if (filters.price?.min !== undefined || filters.price?.max !== undefined) {
    contexts.push({
      type: 'price',
      label: 'Price',
      value: `${filters.price?.min ?? 0}-${filters.price?.max ?? 'max'}`,
    })
  }

  filters.options?.forEach(option => {
    addArrayFilter(contexts, option.type, option.type, option.value)
  })

  return contexts
}

const MERCHANDISE_SORT_MAP: Record<string, Sort_1_0_0['value']> = {
  recommended: 'recommended',
  newArrivals: 'updated_at.desc',
  priceAsc: 'price.asc',
  priceDesc: 'price.desc',
}

export function mapMerchandiseFiltersToSortContext(
  filters: App.MerchandiseFilters,
): Sort_1_0_0 {
  const sortValue = MERCHANDISE_SORT_MAP[filters.sortBy ?? 'recommended'] ?? 'recommended'
  return { value: sortValue }
}

export function hasMerchandiseFilterChanged({
  previousFilters,
  currentFilters,
}: {
  previousFilters?: App.MerchandiseFilters
  currentFilters: App.MerchandiseFilters
}): boolean {
  if (!previousFilters) return false

  const prev = {
    categories: previousFilters.categories,
    brands: previousFilters.brands,
    campaigns: previousFilters.campaigns,
    tags: previousFilters.tags,
    price: previousFilters.price,
    options: previousFilters.options,
  }

  const curr = {
    categories: currentFilters.categories,
    brands: currentFilters.brands,
    campaigns: currentFilters.campaigns,
    tags: currentFilters.tags,
    price: currentFilters.price,
    options: currentFilters.options,
  }

  return JSON.stringify(prev) !== JSON.stringify(curr)
}
